import { brandStorage as localStorage } from '@/utils/brandSession';
import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { MessageCircle, X, Send, Eye } from 'lucide-react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { BACKEND_URL } from '../utils/config';

const API = `${BACKEND_URL}/api`;
const HIDDEN_STORAGE_KEY = 'holly_assistant_hidden';

const HollyAssistant = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isHidden, setIsHidden] = useState(() => {
    try {
      return localStorage.getItem(HIDDEN_STORAGE_KEY) === 'true';
    } catch (e) {
      return false;
    }
  });

  const hideMascot = (e) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    try {
      localStorage.setItem(HIDDEN_STORAGE_KEY, 'true');
    } catch (err) {
      /* ignore */
    }
    setIsHidden(true);
  };

  const showMascot = () => {
    try {
      localStorage.setItem(HIDDEN_STORAGE_KEY, 'false');
    } catch (err) {
      /* ignore */
    }
    setIsHidden(false);
  };
  const [messages, setMessages] = useState([
    {
      role: 'holly',
      content: '👋 Ciao! Sono Holly, la tua assistente virtuale HolidayWorld! Come posso aiutarti oggi?',
      timestamp: new Date()
    }
  ]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const audioContextRef = useRef(null);

  // Create pleasant notification sound using Web Audio API
  const playNotificationSound = () => {
    try {
      // Create audio context if not exists
      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }
      
      const audioContext = audioContextRef.current;
      const now = audioContext.currentTime;
      
      // Create a pleasant two-tone notification (C5 -> E5)
      // First tone
      const oscillator1 = audioContext.createOscillator();
      const gainNode1 = audioContext.createGain();
      
      oscillator1.connect(gainNode1);
      gainNode1.connect(audioContext.destination);
      
      oscillator1.frequency.setValueAtTime(523.25, now); // C5
      oscillator1.type = 'sine'; // Smooth sine wave
      
      gainNode1.gain.setValueAtTime(0, now);
      gainNode1.gain.linearRampToValueAtTime(0.15, now + 0.01);
      gainNode1.gain.exponentialRampToValueAtTime(0.01, now + 0.15);
      
      oscillator1.start(now);
      oscillator1.stop(now + 0.15);
      
      // Second tone (slightly delayed)
      const oscillator2 = audioContext.createOscillator();
      const gainNode2 = audioContext.createGain();
      
      oscillator2.connect(gainNode2);
      gainNode2.connect(audioContext.destination);
      
      oscillator2.frequency.setValueAtTime(659.25, now + 0.1); // E5
      oscillator2.type = 'sine';
      
      gainNode2.gain.setValueAtTime(0, now + 0.1);
      gainNode2.gain.linearRampToValueAtTime(0.12, now + 0.11);
      gainNode2.gain.exponentialRampToValueAtTime(0.01, now + 0.3);
      
      oscillator2.start(now + 0.1);
      oscillator2.stop(now + 0.3);
      
    } catch (e) {
      console.log('Audio notification failed:', e);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const sendMessage = async () => {
    if (!inputMessage.trim() || isLoading) return;

    const userMessage = {
      role: 'user',
      content: inputMessage,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);

    try {
      const token = localStorage.getItem('hw_token');
      const response = await axios.post(
        `${API}/holly/chat`,
        {
          message: inputMessage,
          history: messages.map(m => ({ role: m.role, content: m.content }))
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const hollyMessage = {
        role: 'holly',
        content: response.data.response,
        timestamp: new Date()
      };

      setMessages(prev => [...prev, hollyMessage]);
      
      // Play notification sound
      playNotificationSound();
    } catch (error) {
      console.error('Error sending message to Holly:', error);
      const errorMessage = {
        role: 'holly',
        content: '😔 Mi dispiace, ho avuto un problema. Riprova tra poco!',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const suggestedQuestions = [
    "Come cancello una prenotazione?",
    "Come aggiungo un nuovo immobile?",
    "Cosa significa ADR?",
    "Come interpreto i KPI?",
    "Come genero un report?"
  ];

  return (
    <>
      {/* Mini restore button (when Holly is hidden) */}
      {isHidden && (
        <button
          onClick={showMascot}
          title="Mostra Holly"
          data-testid="holly-assistant-restore-button"
          className="fixed bottom-3 right-3 z-50 w-8 h-8 bg-blue-500/70 hover:bg-blue-600 text-white rounded-full shadow-md flex items-center justify-center opacity-50 hover:opacity-100 transition-all"
        >
          <Eye className="h-4 w-4" />
        </button>
      )}

      {/* Floating Button */}
      {!isOpen && !isHidden && (
        <div className="fixed bottom-6 right-6 z-50">
          <div className="relative group">
            <button
              onClick={() => setIsOpen(true)}
              data-testid="holly-assistant-open-button"
              className="cursor-pointer"
            >
              {/* Mascotte Holly */}
              <div className="w-20 h-20 bg-gradient-to-br from-blue-400 to-blue-600 rounded-full shadow-2xl flex items-center justify-center border-4 border-white transform transition-transform hover:scale-110 overflow-hidden">
                <img 
                  src="/holly.png" 
                  alt="Holly" 
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.parentElement.innerHTML = '<span class="text-3xl">🌍</span>';
                  }}
                />
              </div>
            </button>

            {/* Close (hide) button */}
            <button
              onClick={hideMascot}
              title="Nascondi Holly"
              data-testid="holly-assistant-hide-button"
              className="absolute -top-1 -right-1 w-6 h-6 bg-white hover:bg-red-50 text-gray-700 hover:text-red-600 border border-gray-300 rounded-full shadow-md flex items-center justify-center z-10 transition-colors"
            >
              <X className="h-3.5 w-3.5" />
            </button>

            {/* Tooltip */}
            <div className="absolute bottom-full right-0 mb-2 hidden group-hover:block pointer-events-none">
              <div className="bg-gray-900 text-white text-sm rounded-lg py-2 px-4 whitespace-nowrap shadow-lg">
                Chiedilo a Holly! 💬
                <div className="absolute top-full right-6 w-0 h-0 border-l-8 border-r-8 border-t-8 border-transparent border-t-gray-900"></div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Chat Window */}
      {isOpen && !isHidden && (
        <Card className="fixed bottom-6 right-6 z-50 w-96 h-[600px] shadow-2xl flex flex-col">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-800 text-white p-4 rounded-t-lg flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center overflow-hidden">
                <img 
                  src="/holly.png" 
                  alt="Holly" 
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.parentElement.innerHTML = '<span class="text-2xl">🌍</span>';
                  }}
                />
              </div>
              <div>
                <h3 className="font-bold text-lg">Holly</h3>
                <p className="text-xs text-blue-100">Assistente Virtuale HolidayWorld</p>
              </div>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="hover:bg-blue-700 rounded-full p-1 transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
            {messages.map((message, index) => (
              <div
                key={index}
                className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'} items-start gap-2`}
              >
                {message.role === 'holly' && (
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center overflow-hidden flex-shrink-0 mt-1">
                    <img 
                      src="/holly.png" 
                      alt="Holly" 
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}
                <div
                  className={`max-w-[80%] rounded-lg p-3 ${
                    message.role === 'user'
                      ? 'bg-blue-600 text-white'
                      : 'bg-white text-gray-800 shadow-md border border-gray-200'
                  }`}
                >
                  {message.role === 'holly' && (
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs font-semibold text-blue-600">Holly</span>
                    </div>
                  )}
                  <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                  <p className={`text-xs mt-1 ${message.role === 'user' ? 'text-blue-100' : 'text-gray-400'}`}>
                    {message.timestamp.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}
                  </p>
                </div>
              </div>
            ))}
            
            {isLoading && (
              <div className="flex justify-start">
                <div className="bg-white rounded-lg p-3 shadow-md border border-gray-200">
                  <div className="flex gap-1">
                    <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce"></div>
                    <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                    <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                  </div>
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>

          {/* Suggested Questions (only show at start) */}
          {messages.length === 1 && (
            <div className="px-4 py-2 bg-white border-t border-gray-200">
              <p className="text-xs text-gray-600 mb-2 font-semibold">💡 Domande suggerite:</p>
              <div className="space-y-1">
                {suggestedQuestions.slice(0, 3).map((question, index) => (
                  <button
                    key={index}
                    onClick={() => {
                      setInputMessage(question);
                      setTimeout(() => sendMessage(), 100);
                    }}
                    className="w-full text-left text-xs text-blue-600 hover:text-blue-800 hover:bg-blue-50 p-2 rounded transition-colors"
                  >
                    → {question}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Input */}
          <div className="p-4 bg-white border-t border-gray-200 rounded-b-lg">
            <div className="flex gap-2">
              <input
                type="text"
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Scrivi la tua domanda..."
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={isLoading}
              />
              <Button
                onClick={sendMessage}
                disabled={!inputMessage.trim() || isLoading}
                className="bg-blue-600 hover:bg-blue-700"
              >
                <Send className="h-4 w-4" />
              </Button>
            </div>
            <p className="text-xs text-gray-500 mt-2 text-center">
              Powered by AI 🤖 • Risposte basate sul tuo ruolo
            </p>
          </div>
        </Card>
      )}

    </>
  );
};

export default HollyAssistant;
